import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { useI18n } from "../../i18n";
import { listWorkspaces } from "../../api/collab";
import { CreateWorkspaceModal } from "./CreateWorkspaceModal";

export type Surface = "chat" | "research" | "workspace" | "settings";

const initialOf = (name: string) => [...name.trim()][0]?.toUpperCase() ?? "W";

interface Props {
  surface: Surface;
  onPickChat: () => void;
  onPickResearch: () => void;
  onPickWorkspace: (accountId: string) => void;
  onCreated: () => void;
  /** Compact icon-only trigger for the icon rail (dropdown opens to the right). */
  rail?: boolean;
}

/**
 * Global workspace switcher (app header). Our top-level nav: two default
 * workspaces — 对话 (chat) and 学术 (research) — plus the collaborative
 * workspaces the user created/joined, each opening its workspace page.
 */
export function GlobalWorkspaceSwitcher({ surface, onPickChat, onPickResearch, onPickWorkspace, onCreated, rail }: Props) {
  const { accounts, account, client } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [slugs, setSlugs] = useState<Record<string, string>>({});
  const ref = useRef<HTMLDivElement | null>(null);

  // The first account is the user's personal space (home of 对话/学术); the rest
  // are collaborative workspaces.
  const workspaces = accounts.slice(1);

  const loadSlugs = useCallback(async () => {
    if (!client) return;
    try {
      const { workspaces: ws } = await listWorkspaces(client);
      setSlugs(Object.fromEntries(ws.map((w) => [w.id, w.slug])));
    } catch {
      /* slugs are decorative */
    }
  }, [client]);

  useEffect(() => {
    if (open) void loadSlugs();
  }, [open, loadSlugs]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const nameOf = (n: string, id: string) => (n && n.trim() ? n : `${t("collab.workspace.untitled")} (${id.slice(0, 6)})`);

  const trigger =
    surface === "chat"
      ? { label: t("workspace.chat"), glyph: "💬", cls: "surface" }
      : surface === "research"
        ? { label: t("workspace.researcher"), glyph: "🎓", cls: "surface" }
        : surface === "settings"
          ? { label: t("collab.settings"), glyph: "⚙", cls: "surface" }
          : { label: nameOf(account?.name ?? "", account?.id ?? ""), glyph: initialOf(account?.name ?? "W"), cls: "ws" };

  const pick = (fn: () => void) => () => { fn(); setOpen(false); };

  return (
    <div className={`gws${rail ? " rail" : ""}`} ref={ref}>
      {rail ? (
        <button type="button" className="ws-rail-logo gws-logobtn" onClick={() => setOpen((v) => !v)} title={trigger.label} aria-label={trigger.label} aria-haspopup="menu" aria-expanded={open}>
          <span className="ws-rail-logo-mark">-/</span>
        </button>
      ) : (
        <button type="button" className="gws-trigger" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}>
          <span className={`gws-badge ${trigger.cls}`} aria-hidden>{trigger.glyph}</span>
          <span className="gws-triggertext">
            <span className="gws-current">{trigger.label}</span>
          </span>
          <span className="gws-caret" aria-hidden>▾</span>
        </button>
      )}

      {open ? (
        <div className={`gws-pop${rail ? " railpop" : ""}`} role="menu">
          <div className="gws-heading">{t("collab.workspace.defaults")}</div>
          <button type="button" role="menuitem" className={`gws-item${surface === "chat" ? " active" : ""}`} onClick={pick(onPickChat)}>
            <span className="gws-ico" aria-hidden>💬</span>
            <span className="gws-texts"><span className="gws-name">{t("workspace.chat")}</span></span>
            {surface === "chat" ? <span className="gws-tick" aria-hidden>✓</span> : null}
          </button>
          <button type="button" role="menuitem" className={`gws-item${surface === "research" ? " active" : ""}`} onClick={pick(onPickResearch)}>
            <span className="gws-ico" aria-hidden>🎓</span>
            <span className="gws-texts"><span className="gws-name">{t("workspace.researcher")}</span></span>
            {surface === "research" ? <span className="gws-tick" aria-hidden>✓</span> : null}
          </button>

          {workspaces.length > 0 ? (
            <>
              <div className="gws-sep" />
              <div className="gws-heading">{t("collab.workspace.section")}</div>
              {workspaces.map((a) => {
                const active = surface === "workspace" && account?.id === a.id;
                return (
                  <button key={a.id} type="button" role="menuitem" className={`gws-item${active ? " active" : ""}`} onClick={pick(() => onPickWorkspace(a.id))}>
                    <span className="gws-ico" aria-hidden>{initialOf(a.name)}</span>
                    <span className="gws-texts">
                      <span className="gws-name">{nameOf(a.name, a.id)}</span>
                      {slugs[a.id] ? <span className="gws-sub">/{slugs[a.id]}</span> : null}
                    </span>
                    {active ? <span className="gws-tick" aria-hidden>✓</span> : null}
                  </button>
                );
              })}
            </>
          ) : null}

          <div className="gws-sep" />
          <button type="button" role="menuitem" className="gws-create" onClick={() => { setOpen(false); setCreating(true); }}>
            <span className="gws-plus" aria-hidden>＋</span>
            {t("collab.workspace.new")}
          </button>
        </div>
      ) : null}

      {creating ? (
        <CreateWorkspaceModal
          onClose={() => setCreating(false)}
          onCreated={() => { onCreated(); void loadSlugs(); }}
        />
      ) : null}
    </div>
  );
}
